import React, { useState, useCallback, useRef } from 'react';
import { useRecordContext, useNotify, useRefresh, useGetOne, useGetManyReference, useGetList } from 'react-admin';
import {
  Plus,
  Download,
  RotateCcw,
  XCircle,
  ExternalLink,
  Copy,
  ChevronDown,
  Upload,
  FileText,
  Pencil,
  Eye,
  Mail,
  Printer,
  GitCompare,
} from 'lucide-react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Box,
  CircularProgress,
  TextField,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { Funeral } from '../../types';
import { getToken, getRefreshToken } from '../../authentication/authProvider';
import { getSlotsBySection } from './documentSlots';
import type { DocumentSlotDefinition } from './documentSlots';
import { DocumentSlotRow } from './DocumentSlotRow';
import { openSidebarTab } from '../sidebarTabs';
import { receiptRows } from './receiptRows';
import { IssueDocumentDialog } from './IssueDocumentDialog';
import { ReissueDocumentDialog } from './ReissueDocumentDialog';
import { RevokeDocumentDialog } from './RevokeDocumentDialog';
import { SendAttestationDialog } from './SendAttestationDialog';
import { PDFFormEditor, usePDFEditor } from '../../components/PDFFormEditor';
import { apiFetchResponse } from '../../lib/apiFetch';
import { fallbackFilename, saveResponse } from '../../lib/download';
import { SP3DataViewer } from '../SP3DataViewer';
import { MccdCrosscheck } from '../MccdCrosscheck';
import { extraDocumentTitle, uploadedByLine } from './documentLabels';
import { announcePartnerDocsChanged } from '../../components/dispatch/partnerDocsApi';
import { PARTNER_DOC_RULES } from '../../components/dispatch/partnerDocRequirements';

const PARTNER_DOC_TYPES = new Set(
  PARTNER_DOC_RULES.flatMap((r) => r.requirements.map((q) => q.documentType)),
);

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

interface FuneralDocument {
  id: string;
  funeral_id: number;
  contract_id: number | null;
  document_type: string;
  storage_bucket: string;
  storage_path: string;
  public_url: string | null;
  source: string;
  original_filename: string;
  received_at: string;
  /** Partner-portal uploads (T-108): what the facility called it, and who it is. */
  label?: string | null;
  uploaded_by?: string | null;
}

interface VerifiedDocument {
  id: string;
  verification_id: string;
  document_type: string;
  deceased_full_name: string;
  issued_at: string;
  issuer_name: string;
  status: string;
  version_number: number;
}

export function DocumentsTab() {
  const record = useRecordContext<Funeral>();
  const notify = useNotify();
  const refresh = useRefresh();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [funeralDocs, setFuneralDocs] = useState<FuneralDocument[]>([]);
  // The contract whose receipt is being generated — each paid contract has its own row.
  const [generatingReceiptFor, setGeneratingReceiptFor] = useState<number | null>(null);
  const [verifiedDocs, setVerifiedDocs] = useState<VerifiedDocument[]>([]);
  const [region, setRegion] = useState<string>('quebec');
  const [loading, setLoading] = useState(false);
  const [uploadingType, setUploadingType] = useState<string | null>(null);
  const [otherExpanded, setOtherExpanded] = useState(false);
  const [expandedVerifiedTypes, setExpandedVerifiedTypes] = useState<Set<string>>(new Set());
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);

  // Drop classification dialog
  const [classifyDialogOpen, setClassifyDialogOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  // A filled slot picked in that dialog: replacing it archives the document on
  // file, so it takes a second, explicit click.
  const [confirmReplaceType, setConfirmReplaceType] = useState<string | null>(null);

  // Password-protected PDF: the backend rejects the upload with a 422 until we
  // send the password, so hold the file aside and re-submit it with one.
  const [pdfPasswordPrompt, setPdfPasswordPrompt] = useState<
    { file: File; documentType: string; retry: boolean } | null
  >(null);
  const [pdfPassword, setPdfPassword] = useState('');

  // Verified document dialogs
  const [issueOpen, setIssueOpen] = useState(false);
  const [reissueOpen, setReissueOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [docToSend, setDocToSend] = useState<VerifiedDocument | null>(null);
  const [printOpen, setPrintOpen] = useState(false);
  const [docToPrint, setDocToPrint] = useState<VerifiedDocument | null>(null);
  const [printing, setPrinting] = useState<'digital' | 'wet' | null>(null);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [issueDocType, setIssueDocType] = useState<'POD' | 'POC' | undefined>(undefined);

  // Clients used to pre-populate the send-attestation email
  const { data: primaryClient } = useGetOne(
    'clients',
    { id: record?.primary_client_id },
    { enabled: !!record?.primary_client_id },
  );
  const { data: payingClient } = useGetOne(
    'clients',
    { id: record?.paying_client_id },
    { enabled: !!record?.paying_client_id },
  );
  const { data: authorizedClient } = useGetOne(
    'clients',
    { id: record?.authorized_client_id },
    { enabled: !!record?.authorized_client_id },
  );

  // The case's contracts, used as the fallback source for the receipt's
  // contract id. `funeral_documents.contract_id` is null whenever the signed
  // docs came from an `allContracts` DocuSeal submission, which never carried
  // a contract id — without this the Receipt row would offer no Generate.
  const { data: funeralContracts } = useGetManyReference(
    'contracts',
    { target: 'funeral_id', id: record?.id as number },
    { enabled: !!record?.id },
  );

  // Payments on those contracts: each contract with a completed payment gets
  // its own receipt row. sb_payments is keyed by contract_id, so spell out
  // PostgREST's `in` operator (a plain array filter becomes `eq.1,2`).
  const funeralContractIds = (funeralContracts ?? []).map(c => c.id);
  const { data: contractPayments } = useGetList(
    'sb_payments',
    {
      filter: { 'contract_id@in': `(${funeralContractIds.join(',')})` },
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'created_at', order: 'ASC' },
    },
    { enabled: funeralContractIds.length > 0 },
  );

  // SP3 viewer auto-open after parsing
  const [showSp3Viewer, setShowSp3Viewer] = useState(false);

  // Medical Certificate ↔ Form 15 cross-check (Ontario)
  const [mccdOpen, setMccdOpen] = useState(false);

  // Fillable PDF editor modal
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSlot, setEditorSlot] = useState<DocumentSlotDefinition | null>(null);
  const [editorLoading, setEditorLoading] = useState(false);
  const [savingOverrides, setSavingOverrides] = useState(false);
  const { handlePdfReady, downloadFlattenedPdf, getFieldValues } = usePDFEditor();

  const fetchAllDocuments = useCallback(async () => {
    if (!record?.id) return;
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${PAPERMAN_API_URL}documents/funeral/${record.id}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFuneralDocs(data.funeral_documents || []);
        setVerifiedDocs(data.verified_documents || []);
        setRegion(data.region || 'quebec');
      }
    } catch (e) {
      console.error('Failed to fetch documents', e);
    } finally {
      setLoading(false);
    }
  }, [record?.id]);

  React.useEffect(() => { void fetchAllDocuments(); }, [fetchAllDocuments]);

  // Generate (or regenerate) the Cleo Contract Receipt and store it as a
  // funeral document, then refresh the list so it appears with Download.
  const handleGenerateReceipt = async (contractId: number) => {
    setGeneratingReceiptFor(contractId);
    try {
      const token = await getToken();
      const res = await fetch(`${PAPERMAN_API_URL}contract/${contractId}/receipt/document`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as any));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      notify('Cleo Contract Receipt generated', { type: 'success' });
      await fetchAllDocuments();
    } catch (e: any) {
      notify(`Failed to generate receipt: ${e.message ?? e}`, { type: 'error' });
    } finally {
      setGeneratingReceiptFor(null);
    }
  };

  const handleUpload = async (file: File, documentType: string, password?: string) => {
    if (!record?.id) return;
    setUploadingType(documentType);
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      if (password) formData.append('password', password);
      const res = await fetch(`${PAPERMAN_API_URL}documents/funeral/${record.id}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      // Read the error body once: it can only be consumed a single time.
      const err = res.ok ? null : await res.json().catch(() => ({} as any));
      const pdfPasswordCode =
        res.status === 422 ? err?.code ?? err?.message?.code : undefined;
      if (pdfPasswordCode === 'PDF_PASSWORD_REQUIRED' || pdfPasswordCode === 'PDF_PASSWORD_INCORRECT') {
        // Encrypted PDF: ask for the password (or say the last one was wrong)
        // and re-submit the same file.
        setPdfPassword('');
        setPdfPasswordPrompt({
          file,
          documentType,
          retry: pdfPasswordCode === 'PDF_PASSWORD_INCORRECT',
        });
        return;
      }
      if (res.ok) {
        notify('Document uploaded', { type: 'success' });
        if (PARTNER_DOC_TYPES.has(documentType)) {
          announcePartnerDocsChanged(record.id);
        }
        // If SP3, trigger parsing + refresh funeral data for sp3_data
        if (documentType === 'sp3') {
          notify('Parsing SP3 file...', { type: 'info' });
          try {
            const parseRes = await fetch(`${PAPERMAN_API_URL}sp3/process/funeral/${record.id}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            });
            if (parseRes.ok) {
              notify('SP3 parsed successfully', { type: 'success' });
              refresh();
              // Open SP3 data comparison modal after refresh
              setTimeout(() => {
                setShowSp3Viewer(true);
              }, 500);
            }
          } catch {
            notify('SP3 parsing failed', { type: 'warning' });
          }
        }
        void fetchAllDocuments();
      } else {
        const message = typeof err?.message === 'string' ? err.message : null;
        notify(message || 'Upload failed', { type: 'error' });
      }
    } catch (e) {
      notify('Upload failed', { type: 'error' });
    } finally {
      setUploadingType(null);
    }
  };

  // --- Drop target handlers (counter-based to avoid flicker on child elements) ---
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    if (dragCounter.current === 1) setIsDragOver(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      promptClassification(file);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      promptClassification(file);
    }
    e.target.value = '';
  };

  /** Re-submit the held file with the password the user just typed. */
  const handlePdfPasswordSubmit = async () => {
    if (!pdfPasswordPrompt || !pdfPassword) return;
    const { file, documentType } = pdfPasswordPrompt;
    // Clear first: handleUpload re-opens the prompt itself if the password is
    // wrong, and leaving it open would race with that.
    setPdfPasswordPrompt(null);
    await handleUpload(file, documentType, pdfPassword);
  };

  const handlePdfPasswordCancel = () => {
    setPdfPasswordPrompt(null);
    setPdfPassword('');
    notify('Upload cancelled — the PDF needs its password', { type: 'info' });
  };

  const promptClassification = (file: File) => {
    setPendingFile(file);
    setConfirmReplaceType(null);
    setClassifyDialogOpen(true);
  };

  const handleClassifySelect = (documentType: string) => {
    setClassifyDialogOpen(false);
    setConfirmReplaceType(null);
    if (pendingFile) {
      void handleUpload(pendingFile, documentType);
      setPendingFile(null);
    }
  };

  const handleClassifyCancel = () => {
    setClassifyDialogOpen(false);
    setConfirmReplaceType(null);
    setPendingFile(null);
  };

  // --- Verified document actions ---
  const handleDownloadVerified = async (docId: string) => {
    try {
      const token = await getToken();
      const res = await fetch(`${PAPERMAN_API_URL}documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // A real download named by the backend ("Cleo - Proof of death - Jean
      // Tremblay.pdf"); it used to open a blob: tab that saved as a UUID (T-173).
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await saveResponse(res, 'Cleo - Document.pdf');
    } catch (e) {
      notify('Failed to download', { type: 'error' });
    }
  };

  const handleClosePrint = () => {
    setPrintOpen(false);
    setDocToPrint(null);
  };

  // Print the verified document as-is (digital signature + seal), or as a
  // re-rendered copy without them so it can be hand-signed and embossed.
  const handlePrint = async (variant: 'digital' | 'wet') => {
    if (!docToPrint) return;
    setPrinting(variant);
    try {
      const token = await getToken();
      const endpoint = variant === 'digital'
        ? `${PAPERMAN_API_URL}documents/${docToPrint.id}/download`
        : `${PAPERMAN_API_URL}documents/${docToPrint.id}/print`;
      const res = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to generate PDF');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
      handleClosePrint();
    } catch (e) {
      notify('Failed to prepare document for printing', { type: 'error' });
    } finally {
      setPrinting(null);
    }
  };

  const handleCopyUrl = (verificationId: string) => {
    void navigator.clipboard.writeText(`https://www.cleocremation.com/verify/${verificationId}`);
    notify('Verification URL copied', { type: 'info' });
  };

  // --- Fillable document actions ---
  const getFillableUrl = (slot: DocumentSlotDefinition) =>
    `${PAPERMAN_API_URL}${slot.fillableEndpoint}/${record?.id}`;

  const handleFillableDownload = async (slot: DocumentSlotDefinition) => {
    if (!record?.id) return;
    try {
      const res = await apiFetchResponse(getFillableUrl(slot));
      if (!res.ok) throw new Error('Failed to fetch');
      await saveResponse(res, `${slot.filenamePrefix || slot.label}.pdf`);
      notify(`${slot.label} downloaded`, { type: 'success' });
    } catch (e) {
      notify(`Failed to download ${slot.label}`, { type: 'error' });
    }
  };

  const handleFillableEdit = (slot: DocumentSlotDefinition) => {
    setEditorSlot(slot);
    setEditorLoading(true);
    setEditorOpen(true);
  };

  const handleEditorPdfReady = useCallback((bytes: Uint8Array) => {
    setEditorLoading(false);
    handlePdfReady(bytes);
  }, [handlePdfReady]);

  const handleEditorDownload = async () => {
    if (!editorSlot) return;
    try {
      const filename = `${editorSlot.filenamePrefix || editorSlot.label}.pdf`;
      await downloadFlattenedPdf(filename);
      notify(`${editorSlot.label} downloaded`, { type: 'success' });
    } catch (e) {
      notify('Failed to download', { type: 'error' });
    }
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditorSlot(null);
  };

  // Persist the agent's edits as field overrides. The render endpoint re-applies
  // them on every subsequent render, so the correction sticks — including in the
  // Toronto registrar bundle. Only slots that declare a confirmFormKey can save.
  const handleEditorSave = async () => {
    if (!editorSlot?.confirmFormKey || !record?.id) return;
    const overrides = getFieldValues();
    if (!overrides) {
      notify('PDF is still loading', { type: 'warning' });
      return;
    }
    setSavingOverrides(true);
    try {
      const res = await fetch(`${PAPERMAN_API_URL}form-confirmations/${record.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await getToken()}`,
          'Refresh-Token': await getRefreshToken(),
        },
        body: JSON.stringify({ formKey: editorSlot.confirmFormKey, overrides }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);

      // For the Form 15, also push the edits back to the source funeral/client
      // records so a correction on the form fixes the underlying data, not just
      // the rendered form.
      if (editorSlot.confirmFormKey === 'statement_of_death') {
        try {
          const srcRes = await fetch(`${PAPERMAN_API_URL}statement-of-death/apply-to-source/${record.id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await getToken()}` },
            body: JSON.stringify({ values: overrides }),
          });
          if (srcRes.ok) {
            const { updated } = await srcRes.json();
            if (Array.isArray(updated) && updated.length) {
              notify(`Updated record: ${updated.join(', ')}`, { type: 'info' });
            }
          }
        } catch (e) {
          console.error('Failed to apply Form 15 edits to source', e);
        }
      }

      notify(`${editorSlot.label} saved`, { type: 'success' });
      // Close rather than reconcile: reopening re-fetches from the server with
      // the overrides applied, so what the agent sees next is what the registrar
      // gets.
      handleEditorClose();
      refresh();
    } catch (e) {
      console.error('Failed to save form overrides', e);
      notify(`Failed to save ${editorSlot.label}`, { type: 'error' });
    } finally {
      setSavingOverrides(false);
    }
  };

  if (!record) return null;

  const sections = getSlotsBySection(region);

  // Build classification options: all upload slots (unfilled + filled for replace) + "Other"
  const unfilledUploadSlots = sections.uploads.filter(
    slot => !funeralDocs.find(d => d.document_type === slot.type)
  );
  const filledUploadSlots = sections.uploads.filter(
    slot => funeralDocs.find(d => d.document_type === slot.type)
  );

  const findDoc = (type: string) =>
    funeralDocs.find(d => d.document_type === type) || null;

  const findDocs = (type: string) =>
    funeralDocs
      .filter(d => d.document_type === type)
      .sort((a, b) => new Date(a.received_at).getTime() - new Date(b.received_at).getTime());

  // Uploaded documents that aren't in a known slot (not 'other' from docuseal — those go in Other Documents)
  const uploadedNonSlotDocs = funeralDocs.filter(d => {
    if (d.source !== 'upload') return false;
    // Check if it matches any known upload slot
    const isKnownSlot = sections.uploads.some(s => s.type === d.document_type);
    return !isKnownSlot && d.document_type !== 'other';
  });

  // "Other" documents: type 'other' or uploaded non-slot
  const otherDocs = funeralDocs.filter(d => d.document_type === 'other');
  const allExtraDocs = [...uploadedNonSlotDocs, ...otherDocs];

  const statusColor = (status: string) => {
    switch (status) {
      case 'valid': return 'bg-green-100 text-green-700';
      case 'superseded': return 'bg-gray-100 text-gray-600';
      case 'revoked': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const typeBadge = (type: string) =>
    type === 'POD' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700';

  return (
    <div
      className="relative px-4 py-3 space-y-3 h-full"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={handleFileInputChange}
      />

      {loading ? (
        <div className="text-[13px] text-muted-foreground">Loading...</div>
      ) : (
        <>
          {/* Signed Documents (DocuSeal) */}
          <section>
            <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
              Signed Documents
            </div>
            <div className="space-y-1">
              {sections.signed.flatMap(slot => {
                const docs = findDocs(slot.type);
                if (docs.length <= 1) {
                  return (
                    <DocumentSlotRow
                      key={slot.type}
                      slot={slot}
                      document={docs[0] || null}
                    />
                  );
                }
                return docs.map((doc) => {
                  const label = doc.contract_id
                    ? `${slot.label} ${doc.contract_id}`
                    : `${slot.label} (${new Date(doc.received_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
                  return (
                    <DocumentSlotRow
                      key={doc.id}
                      slot={{ ...slot, label }}
                      document={doc}
                    />
                  );
                });
              })}
            </div>
          </section>

          {/* Cleo Contract Receipt — generated + stored; Download when present,
              Generate when missing (e.g. contracts paid before this existed).
              One row per paid contract: a pre-need that came to fruition has
              its pre-need agreement and its at-need contract (T-160). */}
          {(() => {
            const fallbackContractId = funeralDocs.find(d => d.contract_id != null)?.contract_id
              ?? (funeralContracts?.length
                ? Math.max(...funeralContracts.map(c => Number(c.id)))
                : undefined);
            const rows = receiptRows(
              funeralContracts ?? [],
              contractPayments ?? [],
              findDocs('cleo_contract_receipt'),
              fallbackContractId,
            );
            return (
              <section>
                <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                  Receipt
                </div>
                <div className="space-y-1">
                  {rows.map(row => {
                    const contractId = row.contractId;
                    return (
                      <DocumentSlotRow
                        key={row.key}
                        slot={{ type: 'cleo_contract_receipt', label: row.label, source: 'generated', regions: ['ontario', 'quebec'] }}
                        document={row.document}
                        onGenerate={contractId ? () => void handleGenerateReceipt(contractId) : undefined}
                        generating={generatingReceiptFor != null && generatingReceiptFor === contractId}
                      />
                    );
                  })}
                </div>
              </section>
            );
          })()}

          {/* Benefits (CPP forms — DocuSeal signing flow). Each slot has
              three states: Pending (no document yet), Unsigned (agent
              clicked Ready and saved a flattened PDF), Signed (customer
              signed via DocuSeal). The Survivor's Pension row is hidden
              until there's at least one document, because it doesn't
              apply to funerals without an eligible spouse/children. */}
          {sections.benefits.length > 0 && (() => {
            const benefitRows = sections.benefits
              .map(slot => {
                const signedType = `${slot.type}_signed`;
                const unsignedType = `${slot.type}_unsigned`;
                const signedDoc = findDoc(signedType);
                const unsignedDoc = findDoc(unsignedType);
                const doc = signedDoc || unsignedDoc;
                const hasAny = !!doc;
                const isAlwaysShown = slot.type === 'cpp_death_benefit';
                if (!hasAny && !isAlwaysShown) return null;
                const tone: 'green' | 'amber' | 'gray' = signedDoc
                  ? 'green'
                  : unsignedDoc
                    ? 'amber'
                    : 'gray';
                const label = signedDoc ? 'Signed' : unsignedDoc ? 'Unsigned' : 'Pending';
                return (
                  <DocumentSlotRow
                    key={slot.type}
                    slot={slot}
                    document={doc}
                    badge={{ label, tone }}
                  />
                );
              })
              .filter(Boolean);
            if (benefitRows.length === 0) return null;
            return (
              <section>
                <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                  Benefits
                </div>
                <div className="space-y-1">{benefitRows}</div>
              </section>
            );
          })()}

          {/* Pre-need TruStage flow (T-162): enrollment form, certificate,
              confirmation package. Shown on pre-need cases, or wherever one exists. */}
          {(() => {
            const anyOnFile = sections.preneed.some(slot => findDocs(slot.type).length > 0);
            if (!record?.has_preneed && !anyOnFile) return null;
            return (
              <section>
                <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                  Pre-need
                </div>
                <div className="space-y-1">
                  {sections.preneed.flatMap(slot => {
                    const docs = findDocs(slot.type);
                    if (docs.length === 0)
                      return [
                        <DocumentSlotRow
                          key={slot.type}
                          slot={slot}
                          document={null}
                          missingActions={
                            slot.type === 'trustage_certificate' ? (
                              // Added by hand from the Details tab's pre-need timeline (T-212).
                              <button
                                type="button"
                                className="text-[11px] text-blue-600 hover:text-blue-800"
                                onClick={() => openSidebarTab('details', 'preneed-timeline')}
                              >
                                Add on Details
                              </button>
                            ) : undefined
                          }
                        />,
                      ];
                    return docs.map(doc => <DocumentSlotRow key={doc.id} slot={slot} document={doc} />);
                  })}
                </div>
              </section>
            );
          })()}

          {/* Confirmations the app saved from outside systems (the City's issued
              burial permit, Retraite Québec's send confirmation). Only listed
              once something exists — there is nothing to do with an empty one. */}
          {(() => {
            const rows = sections.generated.flatMap(slot =>
              findDocs(slot.type).map((doc, i, all) => (
                <DocumentSlotRow
                  key={doc.id}
                  slot={all.length > 1
                    ? { ...slot, label: `${slot.label} (${new Date(doc.received_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})` }
                    : slot}
                  document={doc}
                />
              )),
            );
            if (rows.length === 0) return null;
            return (
              <section>
                <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                  Confirmations
                </div>
                <div className="space-y-1">{rows}</div>
              </section>
            );
          })()}

          {/* Required Uploads (region-specific) */}
          {sections.uploads.length > 0 && (
            <section>
              <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                Required Uploads
              </div>
              <div className="space-y-1">
                {sections.uploads.map(slot => (
                  <DocumentSlotRow
                    key={slot.type}
                    slot={slot}
                    document={findDoc(slot.type)}
                    onUpload={(file) => handleUpload(file, slot.type)}
                    uploading={uploadingType === slot.type}
                    extraActions={
                      slot.type === 'sp3' && record?.sp3_data ? (
                        <button
                          onClick={() => setShowSp3Viewer(true)}
                          className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                          title="View SP3 parsed data"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      ) : slot.type === 'medical_certificate' && findDoc('medical_certificate') ? (
                        <button
                          onClick={() => setMccdOpen(true)}
                          className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                          title="Cross-check against Form 15"
                        >
                          <GitCompare className="w-3.5 h-3.5" />
                        </button>
                      ) : undefined
                    }
                  />
                ))}
              </div>
            </section>
          )}

          {/* Uploaded Documents (non-slot) */}
          {allExtraDocs.length > 0 && (
            <section>
              <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                Uploaded Documents
              </div>
              <div className="space-y-1">
                {allExtraDocs.map(doc => (
                  <div key={doc.id} className="flex items-start justify-between rounded-md border px-3 py-1.5 gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] text-foreground break-all">
                        {extraDocumentTitle(doc)}
                      </div>
                      {extraDocumentTitle(doc) !== (doc.original_filename || 'Document') && doc.original_filename && (
                        <div className="text-[11px] text-muted-foreground break-all">{doc.original_filename}</div>
                      )}
                      <div className="text-[11px] text-muted-foreground">
                        {[
                          new Date(doc.received_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                          }),
                          uploadedByLine(doc),
                        ].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button
                        onClick={async () => {
                          try {
                            const token = await getToken();
                            const res = await fetch(`${PAPERMAN_API_URL}documents/funeral-document/${doc.id}/download`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            if (res.ok) {
                              const blob = await res.blob();
                              window.open(URL.createObjectURL(blob), '_blank');
                            }
                          } catch (e) { console.error('Failed to view document', e); }
                        }}
                        className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                        title="View"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const token = await getToken();
                            const res = await fetch(`${PAPERMAN_API_URL}documents/funeral-document/${doc.id}/download`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            if (res.ok) await saveResponse(res, fallbackFilename(doc));
                          } catch (e) { console.error('Failed to download document', e); }
                        }}
                        className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                        title="Download"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Filled Documents (fillable PDFs) */}
          {sections.fillable.length > 0 && (
            <section>
              <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                Filled Documents
              </div>
              <div className="space-y-1">
                {sections.fillable.map(slot => (
                  <div key={slot.type} className="flex items-start justify-between rounded-md border px-3 py-2">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <FileText className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      <span className="text-[13px] font-medium text-foreground break-words">
                        {slot.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <button
                        onClick={() => handleFillableDownload(slot)}
                        className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                        title="Download pre-filled PDF"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleFillableEdit(slot)}
                        className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                        title="Edit in PDF editor"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Verified Documents (POD) */}
          <section>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                Verified Documents
              </span>
              <button
                onClick={() => { setIssueDocType(undefined); setIssueOpen(true); }}
                className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              >
                <Plus className="w-3 h-3" />
                Issue
              </button>
            </div>
            <div className="space-y-1">
              {sections.verified.map(slot => {
                const docType = slot.type as 'POD' | 'POC';
                const validDocs = verifiedDocs.filter(
                  d => d.document_type === docType && d.status === 'valid'
                );
                const previousDocs = verifiedDocs.filter(
                  d => d.document_type === docType && d.status !== 'valid'
                );
                const isExpanded = expandedVerifiedTypes.has(docType);
                const toggleExpand = () => {
                  setExpandedVerifiedTypes(prev => {
                    const next = new Set(prev);
                    if (next.has(docType)) next.delete(docType);
                    else next.add(docType);
                    return next;
                  });
                };

                return (
                  <div key={slot.type}>
                    {validDocs.length > 0 ? (
                      <div className="space-y-1">
                        {validDocs.map((doc, idx) => (
                          <div key={doc.id} className="rounded-md border px-3 py-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${typeBadge(doc.document_type)}`}>
                                  {doc.document_type}
                                </span>
                                <span className="text-[12px] font-mono text-foreground">{doc.verification_id}</span>
                              </div>
                              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                                valid
                              </span>
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                              {new Date(doc.issued_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              {doc.version_number > 1 && <span className="ml-1.5">v{doc.version_number}</span>}
                              <span className="ml-1.5">&middot; {doc.issuer_name}</span>
                            </div>
                            <div className="flex items-center gap-0.5 mt-1">
                              <button
                                onClick={() => handleDownloadVerified(doc.id)}
                                className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                                title="Download PDF"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleCopyUrl(doc.verification_id)}
                                className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                                title="Copy verification URL"
                              >
                                <Copy className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => window.open(`https://www.cleocremation.com/verify/${doc.verification_id}`, '_blank')}
                                className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                                title="Open verification page"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                              </button>
                              {doc.document_type === 'POD' && (
                                <button
                                  onClick={() => { setDocToSend(doc); setSendOpen(true); }}
                                  className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                                  title="Email to client"
                                >
                                  <Mail className="w-3.5 h-3.5" />
                                </button>
                              )}
                              <button
                                onClick={() => { setDocToPrint(doc); setPrintOpen(true); }}
                                className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                                title="Print"
                              >
                                <Printer className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => { setSelectedDoc(doc); setReissueOpen(true); }}
                                className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                                title="Reissue corrected version"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => { setSelectedDoc(doc); setRevokeOpen(true); }}
                                className="p-1 rounded hover:bg-muted/50 text-red-400 hover:text-red-600"
                                title="Revoke"
                              >
                                <XCircle className="w-3.5 h-3.5" />
                              </button>
                              {idx === validDocs.length - 1 && previousDocs.length > 0 && (
                                <button
                                  onClick={toggleExpand}
                                  className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground ml-auto"
                                  title={isExpanded ? 'Hide previous versions' : `Show ${previousDocs.length} previous version${previousDocs.length > 1 ? 's' : ''}`}
                                >
                                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? '' : '-rotate-90'}`} />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                        <button
                          onClick={() => { setIssueDocType(docType); setIssueOpen(true); }}
                          className="w-full flex items-center justify-center gap-1.5 rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-[11px] text-muted-foreground hover:text-foreground hover:border-gray-400"
                        >
                          <Plus className="w-3 h-3" />
                          Issue another {docType}
                        </button>
                      </div>
                    ) : (
                      <DocumentSlotRow
                        slot={slot}
                        document={null}
                        onIssue={() => {
                          setIssueDocType(docType);
                          setIssueOpen(true);
                        }}
                      />
                    )}
                    {isExpanded && previousDocs.length > 0 && (
                      <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
                        {previousDocs.map(doc => (
                          <div key={doc.id} className="rounded-md border border-dashed px-2.5 py-1.5 opacity-70">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <span className="text-[11px] font-mono text-muted-foreground">{doc.verification_id}</span>
                                {doc.version_number > 1 && (
                                  <span className="text-[10px] text-muted-foreground">v{doc.version_number}</span>
                                )}
                              </div>
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${statusColor(doc.status)}`}>
                                {doc.status}
                              </span>
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                              {new Date(doc.issued_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              <span className="ml-1.5">&middot; {doc.issuer_name}</span>
                            </div>
                            <div className="flex items-center gap-0.5 mt-0.5">
                              <button
                                onClick={() => handleDownloadVerified(doc.id)}
                                className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                                title="Download PDF"
                              >
                                <Download className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleCopyUrl(doc.verification_id)}
                                className="p-1 rounded hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                                title="Copy verification URL"
                              >
                                <Copy className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Drop hint / upload button */}
          <div className="pt-1">
            <button
              onClick={() => fileInputRef.current?.click()}
              className={`w-full flex items-center justify-center gap-1.5 rounded-md border border-dashed px-3 py-2 text-[12px] transition-colors ${
                isDragOver
                  ? 'border-blue-400 bg-blue-50 text-blue-600'
                  : 'border-gray-300 text-muted-foreground hover:text-foreground hover:border-gray-400'
              }`}
            >
              <Upload className="w-3.5 h-3.5" />
              {isDragOver ? 'Drop to upload' : 'Drop file here or click to upload'}
            </button>
          </div>
        </>
      )}

      {/* Password prompt for encrypted PDFs (e.g. ServiceOntario e-forms, which
          arrive with a one-time password in the covering email). */}
      <Dialog
        open={!!pdfPasswordPrompt}
        onClose={handlePdfPasswordCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, pb: 0.5 }}>This PDF is password protected</DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 0.5 }}>
          <div className="text-[12px] text-muted-foreground mb-2 truncate">
            {pdfPasswordPrompt?.file.name}
          </div>
          <div className="text-[12px] text-muted-foreground mb-3">
            Enter the password to unlock it. We store an unlocked copy, so it opens
            without a password from here on.
          </div>
          <form
            onSubmit={e => {
              e.preventDefault();
              void handlePdfPasswordSubmit();
            }}
          >
            <TextField
              autoFocus
              fullWidth
              size="small"
              type="password"
              label="PDF password"
              value={pdfPassword}
              onChange={e => setPdfPassword(e.target.value)}
              error={pdfPasswordPrompt?.retry}
              helperText={pdfPasswordPrompt?.retry ? 'That password did not open the PDF.' : ' '}
              inputProps={{ autoComplete: 'off' }}
            />
          </form>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handlePdfPasswordCancel} size="small">Cancel</Button>
          <Button
            onClick={() => void handlePdfPasswordSubmit()}
            variant="contained"
            size="small"
            disabled={!pdfPassword || uploadingType !== null}
          >
            Unlock and upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* Classification dialog */}
      <Dialog open={classifyDialogOpen} onClose={handleClassifyCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, pb: 0.5 }}>What type of document is this?</DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 0.5 }}>
          <div className="text-[12px] text-muted-foreground mb-2 truncate">
            {pendingFile?.name}
          </div>
          {confirmReplaceType ? (
            <div className="text-[13px] text-foreground">
              Replace the {sections.uploads.find(s => s.type === confirmReplaceType)?.label ?? confirmReplaceType} on file
              {findDoc(confirmReplaceType)?.original_filename
                ? <> (<span className="break-all">{findDoc(confirmReplaceType)?.original_filename}</span>)</>
                : null}
              {' '}with this file?
            </div>
          ) : (
          <List dense disablePadding>
            {unfilledUploadSlots.map(slot => (
              <ListItemButton
                key={slot.type}
                onClick={() => handleClassifySelect(slot.type)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemText
                  primary={slot.label}
                  primaryTypographyProps={{ fontSize: 13 }}
                />
              </ListItemButton>
            ))}
            {filledUploadSlots.map(slot => (
              <ListItemButton
                key={slot.type}
                onClick={() => setConfirmReplaceType(slot.type)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemText
                  primary={`${slot.label} (Replace)`}
                  primaryTypographyProps={{ fontSize: 13, color: 'text.secondary' }}
                />
              </ListItemButton>
            ))}
            <ListItemButton
              onClick={() => handleClassifySelect('other')}
              sx={{ borderRadius: 1 }}
            >
              <ListItemText
                primary="Other document"
                primaryTypographyProps={{ fontSize: 13, color: 'text.secondary' }}
              />
            </ListItemButton>
          </List>
          )}
        </DialogContent>
        <DialogActions>
          {confirmReplaceType ? (
            <>
              <Button onClick={() => setConfirmReplaceType(null)} size="small">Back</Button>
              <Button
                onClick={() => handleClassifySelect(confirmReplaceType)}
                variant="contained"
                size="small"
              >
                Replace
              </Button>
            </>
          ) : (
            <Button onClick={handleClassifyCancel} size="small">Cancel</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Fillable PDF editor modal */}
      <Dialog
        open={editorOpen}
        onClose={handleEditorClose}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{editorSlot?.label}</span>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {editorSlot?.confirmFormKey && (
              <Button
                onClick={() => void handleEditorSave()}
                variant="contained"
                color="primary"
                size="small"
                disabled={editorLoading || savingOverrides}
                startIcon={savingOverrides ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                {savingOverrides ? 'Saving…' : 'Save changes'}
              </Button>
            )}
            <Button
              onClick={handleEditorDownload}
              variant={editorSlot?.confirmFormKey ? 'outlined' : 'contained'}
              color="primary"
              size="small"
              disabled={editorLoading}
              startIcon={<DownloadIcon />}
            >
              Download
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ flexGrow: 1, p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
            {editorLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', position: 'absolute', inset: 0, zIndex: 1 }}>
                <CircularProgress />
              </Box>
            )}
            <Box sx={{ height: '100%', overflow: 'auto', display: editorLoading ? 'none' : 'block' }}>
              {editorOpen && editorSlot && (
                <PDFFormEditor
                  pdfUrl={getFillableUrl(editorSlot)}
                  onPdfReady={handleEditorPdfReady}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleEditorClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Print verified document dialog */}
      <Dialog
        open={printOpen}
        onClose={printing ? undefined : handleClosePrint}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, pb: 0.5 }}>Print document</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <div className="text-[12px] text-muted-foreground mb-2 font-mono">
            {docToPrint?.verification_id}
          </div>
          <div className="text-[13px] text-foreground">
            Print with the digital signature and seal, or without them? The
            version without is meant to be signed by hand and embossed
            (&ldquo;Digitally Signed&rdquo; becomes &ldquo;Certified by&rdquo;).
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePrint} size="small" disabled={!!printing}>
            Cancel
          </Button>
          <Button
            onClick={() => void handlePrint('wet')}
            size="small"
            disabled={!!printing}
            startIcon={printing === 'wet' ? <CircularProgress size={14} /> : undefined}
          >
            Without (sign by hand)
          </Button>
          <Button
            onClick={() => void handlePrint('digital')}
            size="small"
            variant="contained"
            disabled={!!printing}
            startIcon={printing === 'digital' ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            With digital signatures
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verified document dialogs */}
      <IssueDocumentDialog
        open={issueOpen}
        onClose={() => setIssueOpen(false)}
        funeral={record}
        onSuccess={fetchAllDocuments}
        defaultDocType={issueDocType}
      />
      <ReissueDocumentDialog
        open={reissueOpen}
        onClose={() => { setReissueOpen(false); setSelectedDoc(null); }}
        document={selectedDoc}
        onSuccess={fetchAllDocuments}
        region={record?.region}
        funeral={record}
      />
      <RevokeDocumentDialog
        open={revokeOpen}
        onClose={() => { setRevokeOpen(false); setSelectedDoc(null); }}
        document={selectedDoc}
        onSuccess={fetchAllDocuments}
      />
      <SendAttestationDialog
        open={sendOpen}
        onClose={() => { setSendOpen(false); setDocToSend(null); }}
        verifiedDoc={docToSend}
        funeral={record}
        primaryClient={primaryClient}
        payingClient={payingClient}
        authorizedClient={authorizedClient}
        funeralDocs={funeralDocs}
      />

      {/* SP3 Data Viewer - auto-opens after SP3 parsing */}
      {record?.sp3_data && (
        <SP3DataViewer
          sp3Data={record.sp3_data}
          autoOpen={showSp3Viewer}
          onClose={() => setShowSp3Viewer(false)}
          hideButton
        />
      )}

      {/* Medical Certificate ↔ Form 15 cross-check (Ontario, warn-only) */}
      {record?.id && (
        <MccdCrosscheck
          funeralId={record.id}
          mccdDocId={findDoc('medical_certificate')?.id}
          open={mccdOpen}
          onClose={() => setMccdOpen(false)}
        />
      )}
    </div>
  );
}
